const RPC_UNAVAILABLE_CODES = new Set(['PGRST202', '42883']);

export function isRegistrationPortalRpcUnavailable(error) {
  if (!error) return false;
  if (RPC_UNAVAILABLE_CODES.has(error.code)) return true;
  const message = String(error.message || '').toLowerCase();
  return message.includes('get_public_registration_portal')
    && (message.includes('could not find') || message.includes('does not exist'));
}

/**
 * Convert the shaped RPC response into the legacy registration object expected
 * by the existing LMS screens. Only participant-visible form values are placed
 * in additional_data; staff-only keys never enter browser state.
 */
export function unwrapRegistrationPortal(payload) {
  if (!payload) return null;

  const registration = payload.registration || {};
  const form = payload.form || {};

  return {
    ...registration,
    id: payload.id || registration.id,
    registration_stage: payload.registration_status || registration.registration_stage,
    rejection_reason: payload.rejection_reason ?? registration.rejection_reason ?? null,
    registration_template_id: form.template_id ?? registration.registration_template_id ?? null,
    additional_data: form.values || {},
    enrollment: payload.enrollment || null,
    payment_status: payload.payment_status || null,
    portal_state: payload.portal_state || null,
    can_edit: payload.can_edit === true,
    form_schema: Array.isArray(form.schema) ? form.schema : [],
    portal_updated_at: payload.updated_at || registration.updated_at || null,
  };
}

/**
 * Transitional read used during the additive rollout. The direct table fallback
 * is allowed only when PostgREST reports that the new function is not deployed.
 */
export async function fetchPublicRegistration(supabase, registrationId) {
  const rpcResult = await supabase.rpc('get_public_registration_portal', {
    p_registration_id: registrationId,
  });

  if (!rpcResult.error) {
    return {
      data: unwrapRegistrationPortal(rpcResult.data),
      error: null,
      source: 'rpc',
    };
  }

  if (!isRegistrationPortalRpcUnavailable(rpcResult.error)) {
    return { data: null, error: rpcResult.error, source: 'rpc' };
  }

  const legacyResult = await supabase
    .from('registration')
    .select('*')
    .eq('id', registrationId)
    .single();

  return {
    data: legacyResult.data,
    error: legacyResult.error,
    source: 'legacy',
  };
}

export async function fetchPublicStudentPackage(supabase, registrationId) {
  return supabase.rpc('get_public_student_package', {
    p_registration_id: registrationId,
  });
}

export async function fetchMyStudentPackages(supabase) {
  return supabase.rpc('get_my_student_packages');
}

export async function updatePublicRegistrationAnswers(
  supabase,
  { registrationId, answers, expectedUpdatedAt },
) {
  const result = await supabase.rpc('update_public_registration_answers', {
    p_registration_id: registrationId,
    p_answers: answers,
    p_expected_updated_at: expectedUpdatedAt,
  });

  return {
    data: result.error ? null : unwrapRegistrationPortal(result.data),
    error: result.error,
  };
}

export async function advancePublicRegistrationJourney(
  supabase,
  { registrationId, expectedUpdatedAt = null },
) {
  const result = await supabase.rpc('advance_public_registration_journey', {
    p_registration_id: registrationId,
    p_expected_updated_at: expectedUpdatedAt,
  });

  return {
    data: result.error ? null : unwrapRegistrationPortal(result.data),
    error: result.error,
  };
}
